import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Activity } from "lucide-react";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const VideoSlot = ({ slotY }) => (
  <div className="lg:col-span-5 flex justify-center lg:justify-end">
    <motion.div
      style={{ y: slotY }}
      initial={{ opacity: 0, y: 60, rotate: 4 }}
      animate={{ opacity: 1, y: 0, rotate: 2 }}
      transition={{ duration: 1.2, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
      data-testid="intro-video-slot"
      className="relative w-80 md:w-[22rem] lg:w-[24rem] aspect-[9/16] rounded-[2.5rem] backdrop-blur-2xl bg-white/10 border border-white/20 shadow-[0_40px_120px_-30px_rgba(255,20,147,0.5)] overflow-hidden"
    >
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute inset-0 flex flex-col"
      >
        <div className="flex items-center justify-between px-5 pt-5 font-tech text-[10px] tracking-[0.25em] uppercase text-white/60">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#FF1493] rec-dot" /> REC
          </span>
          <span>00:00:00</span>
        </div>
        <div className="flex-1 m-4 mt-4 rounded-3xl border border-dashed border-white/25 bg-black/30 flex items-center justify-center">
          <button
            data-testid="intro-video-play-button"
            className="relative w-20 h-20 rounded-full bg-[#FF1493] flex items-center justify-center transition-transform duration-300 hover:scale-110"
            aria-label="Play intro video"
          >
            <span className="absolute inset-0 rounded-full bg-[#FF1493] ping-ring" />
            <Play size={26} className="text-black fill-black ml-1" />
          </button>
        </div>
        <div className="px-5 pb-5 flex items-center justify-between font-tech text-[10px] tracking-[0.2em] uppercase text-white/50">
          <span className="flex items-center gap-2">
            <Activity size={12} strokeWidth={1.5} className="text-[#FF1493]" /> 72 BPM
          </span>
          <span>35,000 FT</span>
        </div>
      </motion.div>
    </motion.div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const slotY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      {/* Airplane window background video with parallax */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <video
          className="w-full h-full object-cover"
          src="/media/clouds.mp4"
          poster="/media/poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          data-testid="hero-background-video"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(255,20,147,0.35),_transparent_55%)]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-black/40" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-28 md:pt-32 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Kinetic headline */}
          <div className="lg:col-span-7 lg:-mt-20">
            <h1 className="font-display font-black leading-[0.92] tracking-tighter">
              <MaskedLine delay={0.45}>
                <span className="block text-[#FF1493] uppercase text-7xl md:text-8xl lg:text-[9rem] tracking-[-0.04em]" data-testid="hero-name">
                  Tanvi
                </span>
              </MaskedLine>
              <MaskedLine delay={0.65}>
                <span className="block text-4xl md:text-5xl lg:text-6xl mt-2">Engineers <span className="italic">the body<span className="text-[#FF1493]">.</span></span></span>
              </MaskedLine>
            </h1>
            <MaskedLine delay={1.1} className="mt-10">
              <span className="flex flex-wrap items-center gap-4">
                <a
                  href="#experience"
                  data-testid="hero-cta-journey"
                  className="rounded-full bg-[#FF1493] text-black font-tech text-xs tracking-[0.2em] uppercase px-8 py-4 transition-transform duration-300 hover:scale-105"
                >
                  Track the journey
                </a>
              </span>
            </MaskedLine>
          </div>

          <VideoSlot slotY={slotY} />
        </div>
      </motion.div>
    </section>
  );
};
